import { isPlaceableInterface } from '@/challenges/definition';
import { assertAuthoritativeCapture } from '@/simulation/native/authority';
import { continuationForCapture } from '@/simulation/native/continuationMetadata';
import type Context from '@/context';
import { NO_NODE_HANDLE } from '@/models/logic/RuntimeHandles';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { makeAutoObservable, observable } from 'mobx';
import { createChipId, createInterfaceLinkInstanceId } from '@/types/ids';
import type { ChipId, InterfaceLinkInstanceId } from '@/types/ids';
import type {
  IChipInstanceModel,
  IChipModel,
  ILogicCircuitModel,
  ILogicGraph,
  IStructureCircuitModel,
} from '@/interfaces/models';
import { ChipInstanceModel } from './ChipInstanceModel';
import { TileInstance } from './tiles/TileInstance';
import { TileSideIO } from './tiles/TileDefinition';
import { CircuitType, oppositeCircuitType, Side } from '@/schemas/common';
import { InterfaceTileState, TileSideIOType } from '@/schemas/tiles';
import { LogicGraph } from './logic/LogicGraph';
import { TemplateGraphBuilder } from './logic/TemplateGraphBuilder';
import { TileType } from './TileTypes';
import { tileDefinitionRegistry } from './tiles/TileRegistry';
import type { InterfaceLinkDto, ChipModelDto } from '@/schemas/chip';
import { PixelArtData } from '@/models/PixelArtData';
import type { PointData } from '@/models/Point';
import { isRecursiveBoundary } from '@/utils/recursiveChipBoundary';
import { ownerRuntimeSnapshot, ownerRuntimeNodeIds } from '@/utils/ownerRuntimeSnapshot';
import { liveGraphRecords } from '@/services/projectSnapshot/GraphRecordSource';
import type {
  ChipSnapshot,
  ProjectSnapshotBuilder,
} from '@/services/projectSnapshot/ProjectSnapshot';
import { observeSnapshotInput } from '@/services/projectSnapshot/observeSnapshotInput';
import { confirmAllocatedInterfaceLink } from './tiles/interfaceLabelAllocation';
import { invalidateInterfaceLinkTiles } from './tiles/InterfaceTile';

export enum InterfaceType {
  SINGLE,
  MULTI,
  DISPLAY,
  BUNDLE,
}

export enum SIInputMode {
  NONE = 'none',
  LEVER = 'lever',
  BUTTON = 'button',
}

export enum MIDisplayMode {
  NONE = 'none',
  GRADIENT = 'gradient',
  IMAGE = 'image',
  PIXEL_ART = 'pixel_art',
}

export interface MISideConfig {
  active: boolean;
  forceConnection: boolean;
}

export const DEFAULT_MI_SIDE_CONFIG: Readonly<MISideConfig> = {
  active: true,
  forceConnection: false,
};

function createDefaultSideConfig(): Record<Side, MISideConfig> {
  return {
    [Side.TOP]: { ...DEFAULT_MI_SIDE_CONFIG },
    [Side.RIGHT]: { ...DEFAULT_MI_SIDE_CONFIG },
    [Side.BOTTOM]: { ...DEFAULT_MI_SIDE_CONFIG },
    [Side.LEFT]: { ...DEFAULT_MI_SIDE_CONFIG },
  };
}

function isDefaultSideConfig(config: Record<Side, MISideConfig>): boolean {
  return [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT].every(
    (side) =>
      config[side].active === DEFAULT_MI_SIDE_CONFIG.active &&
      config[side].forceConnection === DEFAULT_MI_SIDE_CONFIG.forceConnection,
  );
}

export class InterfaceLink {
  id = createInterfaceLinkInstanceId();

  tiles: Partial<Record<CircuitType, TileInstance<InterfaceTileState<unknown>>>> = {};
  sideLabels: Partial<Record<Side, string>> = {};
  /** Exact historical scalar selections; never used for bundle matching. */
  selectorSignalIds?: string[] = undefined;
  sideConfig: Record<Side, MISideConfig> = createDefaultSideConfig();

  inputMode: SIInputMode = SIInputMode.NONE;
  labelHidden = false;
  displayMode: MIDisplayMode = MIDisplayMode.NONE;
  gradientColor: string | null = null;
  stateColors: Map<number, string> = new Map();
  stateImages: Map<number, string> = new Map();
  statePixelArt: Map<number, PixelArtData> = new Map();

  get hasLogic(): boolean {
    return this.tiles[CircuitType.ChipLogic] !== undefined;
  }

  get hasStructure(): boolean {
    return this.tiles[CircuitType.ChipStructure] !== undefined;
  }

  get isLinked(): boolean {
    return this.hasLogic && this.hasStructure;
  }

  computeEffectiveIoSides(): Record<Side, TileSideIO> {
    const result = {} as Record<Side, TileSideIO>;
    for (const side of [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]) {
      const cfg = this.sideConfig[side];
      if (cfg.active) {
        result[side] = {
          type: TileSideIOType.INPUT_OUTPUT,
          forceConnection: cfg.forceConnection,
        };
      } else {
        result[side] = { type: TileSideIOType.NONE, forceConnection: false };
      }
    }
    return result;
  }

  syncTileIoSides(): void {
    const ioSides = this.computeEffectiveIoSides();
    for (const tile of Object.values(this.tiles)) {
      if (tile) {
        tile.effectiveIoSides = ioSides;
      }
    }
  }

  constructor(
    public type: InterfaceType,
    public label: string,
    public color: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get dto(): InterfaceLinkDto {
    const dto: InterfaceLinkDto = {
      id: this.id,
      type: this.type as number,
      label: this.label,
      color: this.color,
      logicTileId: this.tiles[CircuitType.ChipLogic]?.id,
      structureTileId: this.tiles[CircuitType.ChipStructure]?.id,
    };
    if (this.selectorSignalIds) dto.selectorSignalIds = [...this.selectorSignalIds];
    if (Object.keys(this.sideLabels).length > 0) {
      dto.sideLabels = { ...this.sideLabels };
    }
    if (this.inputMode !== SIInputMode.NONE) {
      dto.inputMode = this.inputMode;
    }
    if (this.labelHidden) {
      dto.labelHidden = true;
    }
    if (this.displayMode !== MIDisplayMode.NONE) {
      dto.displayMode = this.displayMode;
    }
    if (this.gradientColor !== null) {
      dto.gradientColor = this.gradientColor;
    }
    if (this.stateColors.size > 0) {
      const colors: Record<string, string> = {};
      for (const [k, v] of this.stateColors) {
        colors[String(k)] = v;
      }
      dto.stateColors = colors;
    }
    if (this.stateImages.size > 0) {
      const images: Record<string, string> = {};
      for (const [k, v] of this.stateImages) {
        images[String(k)] = v;
      }
      dto.stateImages = images;
    }
    if (this.statePixelArt.size > 0) {
      const pixelArts: Record<string, string> = {};
      for (const [k, v] of this.statePixelArt) {
        if (!v.isEmpty) pixelArts[String(k)] = v.toBase64();
      }
      if (Object.keys(pixelArts).length > 0) {
        dto.statePixelArt = pixelArts;
      }
    }
    if (!isDefaultSideConfig(this.sideConfig)) {
      const cfg: Record<string, { active: boolean; forceConnection: boolean }> = {};
      for (const side of [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT]) {
        const s = this.sideConfig[side];
        if (
          s.active !== DEFAULT_MI_SIDE_CONFIG.active ||
          s.forceConnection !== DEFAULT_MI_SIDE_CONFIG.forceConnection
        ) {
          cfg[side] = { active: s.active, forceConnection: s.forceConnection };
        }
      }
      dto.sideConfig = cfg;
    }
    return dto;
  }

  static fromDto(dto: InterfaceLinkDto): InterfaceLink {
    const link = new InterfaceLink(dto.type as InterfaceType, dto.label, dto.color);
    link.id = dto.id as InterfaceLinkInstanceId;
    link.selectorSignalIds = dto.selectorSignalIds
      ? [...dto.selectorSignalIds]
      : undefined;
    if (dto.sideLabels) {
      link.sideLabels = { ...dto.sideLabels } as Partial<Record<Side, string>>;
    }
    if (dto.inputMode) {
      link.inputMode = dto.inputMode as SIInputMode;
    }
    if (dto.labelHidden) {
      link.labelHidden = dto.labelHidden;
    }
    if (dto.displayMode) {
      link.displayMode = dto.displayMode as MIDisplayMode;
    }
    if (dto.gradientColor !== undefined) {
      link.gradientColor = dto.gradientColor ?? null;
    }
    if (dto.stateColors) {
      for (const [k, v] of Object.entries(dto.stateColors)) {
        link.stateColors.set(Number(k), v);
      }
    }
    if (dto.stateImages) {
      for (const [k, v] of Object.entries(dto.stateImages)) {
        link.stateImages.set(Number(k), v);
      }
    }
    if (dto.statePixelArt) {
      for (const [k, v] of Object.entries(dto.statePixelArt)) {
        link.statePixelArt.set(Number(k), PixelArtData.fromBase64(v));
      }
    }
    if (dto.sideConfig) {
      for (const [side, cfg] of Object.entries(dto.sideConfig)) {
        link.sideConfig[side as Side] = {
          active: cfg.active,
          forceConnection: cfg.forceConnection,
        };
      }
    }
    return link;
  }
}

/** Model of Chip that will be reused */
export class ChipModel implements IChipModel {
  #nativeSnapshotModel = undefined;
  /** Brand check rejects opaque proxies before inspecting any snapshot hooks. */
  static isNativeSnapshotModel(value: unknown): value is ChipModel {
    return (
      !!value &&
      typeof value === 'object' &&
      #nativeSnapshotModel in value &&
      Object.getPrototypeOf(value) === ChipModel.prototype
    );
  }

  static #nativeGraphHooks = {
    boundaryNodes: LogicGraph.prototype.boundaryNodes,
    edgeDto: LogicGraph.prototype.edgeDto,
    nodeIdText: LogicGraph.prototype.nodeIdText,
  };
  static #native_captureSnapshot = ChipModel.prototype.captureSnapshot;
  static #native_snapshotMetadata = ChipModel.prototype.snapshotMetadata;
  kind?: 'bundle-adapter';
  templateType?: 'logic' | 'structure';
  templateOrigin?: PointData;
  adapterSignals: Record<string, string> = {};
  adapterSignalIds: string[] = [];
  adapterSignalLabels: Record<string, string> = {};
  adapterSignalAliases: Record<string, string[]> = {};
  folder: string;
  id: ChipId;

  name: string;
  description: string;
  /** The HSL color of the chip (e.g., "hsl(180, 70%, 50%)") */
  color: string;
  /** data that will be copied to create new ChipInstances */
  editingInstance: IChipInstanceModel;
  logicCircuit: ILogicCircuitModel;
  structureCircuit: IStructureCircuitModel;
  interfaceLinkMap: Map<InterfaceLinkInstanceId, InterfaceLink> = new Map();
  /** Unreferenced Main/draft editors can omit templates; explicit requests end deferral. */
  deferUnusedTemplate = false;
  templateGraph: ILogicGraph | null = null;
  templateGraphVersion = 0;
  /** Runtime-only lookup for read-only inspection of placed instances. */
  templateTileNodeMap = new Map<string, Partial<Record<Side, NodeHandle>>>();
  templateInstanceNodeMaps = new Map<string, Map<NodeHandle, NodeHandle>>();
  templateTileBundleMap = new Map<
    string,
    Pick<
      TileInstance,
      'laneNodeIds' | 'laneSignalIds' | 'laneSignalLabels' | 'bundleSignalLabels'
    >
  >();
  interfaceNodeMap: Map<InterfaceLinkInstanceId, Partial<Record<Side, NodeHandle>>> =
    new Map();
  private _adapterFingerprint = '';
  private _isRebuildingTemplate = false;
  private _templateBuilder: TemplateGraphBuilder;
  private readonly continuationContext?: Context;

  get directChildChips(): IChipModel[] {
    return Array.from(this.logicCircuit.chipInstances.values())
      .map((instance) => instance.chip)
      .filter((chip): chip is IChipModel => Boolean(chip));
  }

  get recursiveChildChips(): IChipModel[] {
    const visited = new Set<ChipId>();
    const result: IChipModel[] = [];
    const stack: IChipModel[] = [...this.directChildChips];

    while (stack.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const next = stack.pop()!;

      if (visited.has(next.id)) {
        continue;
      }

      visited.add(next.id);
      result.push(next);

      const nextChildren = Array.from(next.logicCircuit.chipInstances.values())
        .map((instance) => instance.chip)
        .filter((chip): chip is IChipModel => Boolean(chip));

      for (const child of nextChildren) {
        if (!visited.has(child.id)) {
          stack.push(child);
        }
      }
    }

    return result;
  }

  combinedColorDisplay = false;

  get interfaceLinks(): InterfaceLink[] {
    return Array.from(this.interfaceLinkMap.values());
  }

  getInterfaceLinkByLabel(label: string): InterfaceLink | undefined {
    for (const link of this.interfaceLinkMap.values()) {
      if (link.label === label) {
        return link;
      }
    }
    return undefined;
  }

  createInterfaceLink(type: InterfaceType, label?: string, color = '0xff0000') {
    if (!label) {
      let i = 1;
      while (this.getInterfaceLinkByLabel(String(i))) {
        i++;
      }
      label = String(i);
    }

    const newLink = new InterfaceLink(type, label, color);
    this.interfaceLinkMap.set(newLink.id, newLink);
    confirmAllocatedInterfaceLink(this, newLink);
    return newLink;
  }

  constructor({
    context,
    id,
    name,
    kind,
    templateType,
    templateOrigin,
    description = '',
    folder = '',
    combinedColorDisplay = false,
    color,
    editingInstance,
    logicCircuit,
    structureCircuit,
  }: {
    context?: Context;
    id?: ChipId;
    name: string;
    kind?: 'bundle-adapter';
    templateType?: 'logic' | 'structure';
    templateOrigin?: PointData;
    description?: string;
    folder?: string;
    combinedColorDisplay?: boolean;
    color: string;
    editingInstance: IChipInstanceModel;
    logicCircuit: ILogicCircuitModel;
    structureCircuit: IStructureCircuitModel;
  }) {
    this.continuationContext = context;
    this.id = id ?? createChipId();
    this.name = name;
    this.kind = kind;
    this.templateType = templateType;
    this.templateOrigin = templateOrigin ? { ...templateOrigin } : undefined;
    this.description = description;
    this.folder = folder.trim();
    this.color = color;
    this.combinedColorDisplay = combinedColorDisplay;
    this.logicCircuit = logicCircuit;
    this._templateBuilder = new TemplateGraphBuilder(logicCircuit.logicGraph.ids);
    this.logicCircuit.recursionOwnerId = this.id;
    this.structureCircuit = structureCircuit;
    this.editingInstance = editingInstance;
    if (this.editingInstance.chipId !== this.id) {
      this.editingInstance.chipId = this.id;
    }
    makeAutoObservable(
      this,
      {
        templateTileNodeMap: observable.ref,
        templateInstanceNodeMaps: observable.ref,
        templateTileBundleMap: observable.ref,
        _templateBuilder: false,
        continuationContext: false,
        deferUnusedTemplate: false,
        _adapterFingerprint: false,
        _isRebuildingTemplate: false,
        _rebuildTemplateGraphInnerSteps: false,
        rebuildTemplateGraphSteps: false,
        dtoSteps: false,
        dto: false,
        captureSnapshot: false,
        snapshotMetadata: false,
        trackSnapshotDependencies: false,
        observeSnapshotInputs: false,
      } as any,
      { autoBind: true },
    );

    // Keep the imported editing graph until restoration has consumed its state.
    // Once the live graph is ready, both editing owners reference that graph.
    const editingCircuit = this.logicCircuit;
    editingCircuit.onGraphReady = (graph) => {
      if (this.logicCircuit === editingCircuit)
        ChipInstanceModel.adoptEditingGraph(this.editingInstance, graph);
    };
    // Keep templateGraph in sync: rebuild after any logicCircuit graph rebuild
    this.logicCircuit.onAfterRebuild = () => {
      if (!this.deferUnusedTemplate) this.rebuildTemplateGraph();
    };
  }

  addInterfaceTile(
    linkId: InterfaceLinkInstanceId,
    tile: TileInstance,
    circuitType: CircuitType,
  ): void {
    const link = this.interfaceLinkMap.get(linkId);
    if (link) {
      link.tiles[circuitType] = tile as TileInstance<InterfaceTileState<unknown>>;
    }
  }

  renameInterfaceLink(linkId: InterfaceLinkInstanceId, newLabel: string): void {
    const link = this.interfaceLinkMap.get(linkId);
    if (link) {
      const oldLabel = link.label.trim();
      const targetLabel = newLabel.trim();
      const oldLabelRemains = this.interfaceLinks.some(
        (other) =>
          other.id !== link.id &&
          other.type === InterfaceType.SINGLE &&
          other.tiles[CircuitType.ChipStructure] &&
          other.label.trim() === oldLabel,
      );
      const targetLabelRemains = this.interfaceLinks.some(
        (other) =>
          other.id !== link.id &&
          other.type === InterfaceType.SINGLE &&
          other.tiles[CircuitType.ChipStructure] &&
          other.label.trim() === targetLabel,
      );
      if (
        this.kind === 'bundle-adapter' &&
        link.type === InterfaceType.SINGLE &&
        oldLabel !== targetLabel &&
        !oldLabelRemains &&
        Object.hasOwn(this.adapterSignals, oldLabel)
      ) {
        if (targetLabel && !targetLabelRemains) {
          Object.defineProperty(this.adapterSignals, targetLabel, {
            value: this.adapterSignals[oldLabel],
            enumerable: true,
            writable: true,
            configurable: true,
          });
        }
        delete this.adapterSignals[oldLabel];
      }
      link.label = newLabel;
      // Labels are read when Pixi artwork is drawn, outside React's observer.
      // Invalidate only this link's placed tiles for every rename entry point.
      invalidateInterfaceLinkTiles(link);
      if (this.kind === 'bundle-adapter') this.rebuildTemplateGraph();
    }
  }

  removeInterfaceTile(linkId: InterfaceLinkInstanceId, circuitType: CircuitType): void {
    const link = this.interfaceLinkMap.get(linkId);
    if (!link) {
      return;
    }

    const otherCircuitType = oppositeCircuitType(circuitType);
    const context = this.continuationContext;
    const required =
      context?.mainChip === this &&
      (context.challengeAuthoring ||
        (context.challenge && isPlaceableInterface(context.challenge, link.label)));
    if (link.tiles[otherCircuitType] || required) {
      delete link.tiles[circuitType];
    } else {
      this.interfaceLinkMap.delete(linkId);
    }
  }

  get dto(): ChipModelDto {
    assertAuthoritativeCapture(this.logicCircuit);
    this.logicCircuit.flushPendingVisuals?.();
    // Track authoritative runtime changes even when frame-sampled visuals repeat.
    void this.logicCircuit.runtimeRevision;
    const runtime = ownerRuntimeSnapshot(
      this.logicCircuit.logicGraph,
      this.logicCircuit.chipInstances.values(),
      this.logicCircuit.circuitModel.tiles,
    );
    return {
      ...this.snapshotMetadata(),
      chipInstanceDtos: Array.from(this.logicCircuit.chipInstances.values()).map(
        (instance) => instance.dto,
      ),
      editingInstanceDto: this.editingInstance.toDto
        ? this.editingInstance.toDto(this.logicCircuit.logicGraph, runtime)
        : {
            ...this.editingInstance.dto,
            logicGraphDto: runtime,
          },
    };
  }

  private snapshotMetadata(
    includeContinuation = true,
  ): Omit<ChipModelDto, 'chipInstanceDtos' | 'editingInstanceDto'> {
    const continuation = includeContinuation
      ? continuationForCapture(this.continuationContext, this)
      : undefined;
    return {
      id: this.id,
      kind: this.kind,
      templateType: this.templateType,
      templateOrigin: this.templateOrigin ? { ...this.templateOrigin } : undefined,
      adapterSignals:
        this.kind === 'bundle-adapter' ? { ...this.adapterSignals } : undefined,
      name: this.name,
      description: this.description,
      folder: this.folder || undefined,
      color: this.color,
      combinedColorDisplay: this.combinedColorDisplay || undefined,
      logicCircuitDto: this.logicCircuit.circuitModel.dto,
      structureCircuitDto: this.structureCircuit.circuitModel.dto,
      structureOriginLocationDto: this.structureCircuit.originLocation.data,
      interfaceLinks: this.interfaceLinks.map((link) => link.dto),
      ...(continuation ? { nativeContinuation: continuation } : {}),
    };
  }

  captureSnapshot(builder: ProjectSnapshotBuilder): ChipSnapshot {
    assertAuthoritativeCapture(this.logicCircuit);
    this.logicCircuit.flushPendingVisuals?.();
    void this.logicCircuit.runtimeRevision;
    const instances = Array.from(this.logicCircuit.chipInstances.values());
    if (
      !this.editingInstance.captureSnapshot ||
      instances.some((instance) => !instance.captureSnapshot)
    )
      throw new Error('This chip instance does not support current snapshot capture.');
    const graph = this.logicCircuit.logicGraph;
    const retained = ownerRuntimeNodeIds(
      graph,
      instances,
      this.logicCircuit.circuitModel.tiles,
    );
    const runtime = builder.graph(liveGraphRecords(graph, retained));
    return {
      ...this.snapshotMetadata(),
      chipInstanceDtos: instances.map((instance) => instance.captureSnapshot!(builder)),
      editingInstanceDto: this.editingInstance.captureSnapshot(builder, graph, runtime),
    };
  }

  /** Retain authored DTO computed caches and runtime revisions without reading every node. */
  observeSnapshotInputs(): boolean {
    const graph = this.logicCircuit.logicGraph;
    if (
      !(graph instanceof LogicGraph) ||
      graph.constructor !== LogicGraph ||
      !graph.isTopologyTracked ||
      graph.boundaryNodes !== ChipModel.#nativeGraphHooks.boundaryNodes ||
      graph.edgeDto !== ChipModel.#nativeGraphHooks.edgeDto ||
      graph.nodeIdText !== ChipModel.#nativeGraphHooks.nodeIdText
    )
      return false;
    if (
      this.captureSnapshot !== ChipModel.#native_captureSnapshot ||
      this.snapshotMetadata !== ChipModel.#native_snapshotMetadata
    )
      return false;
    this.logicCircuit.flushPendingVisuals?.();
    void this.logicCircuit.runtimeRevision;
    void this.logicCircuit.logicGraph;
    void this.snapshotMetadata(false);
    for (const instance of this.logicCircuit.chipInstances.values())
      if (instance.observeSnapshotInputs?.() !== true) return false;
    return this.editingInstance.observeSnapshotInputs?.() === true;
  }

  /** Error recovery only: keep the observable authoring inputs tracked without
   * taking another potentially failing runtime snapshot. */
  trackSnapshotDependencies(): void {
    void this.logicCircuit.runtimeRevision;
    void this.id;
    void this.kind;
    void this.templateType;
    observeSnapshotInput(this.templateOrigin);
    void this.name;
    void this.description;
    void this.folder;
    void this.color;
    void this.combinedColorDisplay;
    observeSnapshotInput(this.adapterSignals);
    void this.structureCircuit.originLocation.x;
    void this.structureCircuit.originLocation.y;
    for (const circuit of [
      this.logicCircuit.circuitModel,
      this.structureCircuit.circuitModel,
    ]) {
      void circuit.id;
      void circuit.circuitType;
      observeSnapshotInput(circuit.labels?.tags);
      observeSnapshotInput(circuit.labels?.sections);
      void circuit.connectionModel.topologyVersion;
      for (const tile of circuit.tiles) {
        try {
          void tile.id;
          void tile.tileType;
          observeSnapshotInput(tile.meta);
          observeSnapshotInput(tile.state);
          observeSnapshotInput(tile.logicNodeIds);
          void tile.globalTilePosition.chipInstanceId;
          void tile.globalTilePosition.circuitType;
          void tile.globalTilePosition.location.x;
          void tile.globalTilePosition.location.y;
        } catch {
          /* Continue tracking the remaining authored inputs. */
        }
      }
    }
    for (const link of this.interfaceLinks) {
      void link.tiles[CircuitType.ChipLogic]?.id;
      void link.tiles[CircuitType.ChipStructure]?.id;
      for (const key of Object.keys(link))
        if (key !== 'tiles') {
          try {
            observeSnapshotInput((link as unknown as Record<string, unknown>)[key]);
          } catch {
            /* Continue tracking independent interface settings. */
          }
        }
    }
    for (const instance of this.logicCircuit.chipInstances.values()) {
      try {
        instance.trackSnapshotDependencies?.();
      } catch {
        /* Continue tracking later instances. */
      }
    }
    this.editingInstance.trackSnapshotDependencies?.();
  }

  *dtoSteps(): Generator<void, ChipModelDto> {
    assertAuthoritativeCapture(this.logicCircuit);
    this.logicCircuit.flushPendingVisuals?.();
    // Track authoritative runtime changes even when frame-sampled visuals repeat.
    void this.logicCircuit.runtimeRevision;
    const logicCircuitDto = this.logicCircuit.circuitModel.toDtoSteps
      ? yield* this.logicCircuit.circuitModel.toDtoSteps()
      : this.logicCircuit.circuitModel.dto;
    const structureCircuitDto = this.structureCircuit.circuitModel.toDtoSteps
      ? yield* this.structureCircuit.circuitModel.toDtoSteps()
      : this.structureCircuit.circuitModel.dto;
    const runtime = ownerRuntimeSnapshot(
      this.logicCircuit.logicGraph,
      this.logicCircuit.chipInstances.values(),
      this.logicCircuit.circuitModel.tiles,
    );
    return {
      id: this.id,
      kind: this.kind,
      templateType: this.templateType,
      templateOrigin: this.templateOrigin ? { ...this.templateOrigin } : undefined,
      adapterSignals:
        this.kind === 'bundle-adapter' ? { ...this.adapterSignals } : undefined,
      name: this.name,
      description: this.description,
      folder: this.folder || undefined,
      color: this.color,
      combinedColorDisplay: this.combinedColorDisplay || undefined,
      logicCircuitDto,
      structureCircuitDto,
      structureOriginLocationDto: this.structureCircuit.originLocation.data,
      interfaceLinks: this.interfaceLinks.map((link) => link.dto),
      chipInstanceDtos: Array.from(this.logicCircuit.chipInstances.values()).map(
        (instance) => instance.dto,
      ),
      editingInstanceDto: this.editingInstance.toDto
        ? this.editingInstance.toDto(this.logicCircuit.logicGraph, runtime)
        : {
            ...this.editingInstance.dto,
            logicGraphDto: runtime,
          },
    };
  }

  rebuildTemplateGraph(): void {
    const steps = this.rebuildTemplateGraphSteps();
    while (!steps.next().done) {
      /* synchronous editor updates retain their behavior */
    }
  }

  *rebuildTemplateGraphSteps(): Generator<void> {
    this.deferUnusedTemplate = false;
    // Guard: prevent re-entrant calls during an ongoing rebuild
    if (this._isRebuildingTemplate) return;
    this._isRebuildingTemplate = true;
    try {
      yield* this._rebuildTemplateGraphInnerSteps();
    } finally {
      this._isRebuildingTemplate = false;
    }
  }

  private *_rebuildTemplateGraphInnerSteps(): Generator<void> {
    if (this.kind === 'bundle-adapter') {
      this.rebuildAdapterGraph();
      return;
    }
    const internalTileManager = this.logicCircuit.circuitModel.tileManagerModel;
    const internalTiles = internalTileManager.tiles;

    if (internalTiles.length === 0) {
      if (this.templateGraph !== null) {
        this.templateGraph = null;
        this.templateTileNodeMap = new Map();
        this.templateInstanceNodeMaps = new Map();
        this.templateTileBundleMap = new Map();
        this.interfaceNodeMap = new Map();
        this.templateGraphVersion++;
      }
      return;
    }

    for (const link of this.interfaceLinks) {
      if (link.type === InterfaceType.MULTI) link.syncTileIoSides();
    }
    const built = yield* this._templateBuilder.rebuildSteps(
      internalTileManager,
      this.logicCircuit.circuitModel.connectionModel,
      (this.logicCircuit.circuitModel.chips ?? []).filter(
        (instance) =>
          !isRecursiveBoundary(this.logicCircuit.recursionOwnerId ?? this.id, instance),
      ),
    );
    for (const tile of built.tiles) {
      if (!tile.logicNodeIds || tile.meta.partOfChip) continue;
      tileDefinitionRegistry
        .get(tile.tileType)
        .syncStateToNodes?.({ instance: tile, graph: built.graph });
    }
    const newInterfaceNodeMap = new Map<
      InterfaceLinkInstanceId,
      Partial<Record<Side, NodeHandle>>
    >();
    for (const [linkId, link] of this.interfaceLinkMap) {
      const liveTile = link.tiles[CircuitType.ChipLogic];
      const templateTile = liveTile && built.byId.get(liveTile.id);
      if (templateTile?.logicNodeIds) {
        const bindings = { ...templateTile.logicNodeIds };
        // Lowered scalar bundle wires retain a diode for display observation.
        // A placed scalar interface must connect to the transport leaf itself,
        // otherwise its parent can observe the child but cannot drive it.
        for (const side of Object.values(Side)) {
          const node = built.graph.nodes.get(bindings[side] ?? NO_NODE_HANDLE);
          if (!node?.isBundleObserver) continue;
          const sources = built.graph.getEdgesTo(node.id);
          if (
            sources.length === 1 &&
            built.graph.nodes.get(sources[0].sourceNodeId)?.type === 'WIRE'
          )
            bindings[side] = sources[0].sourceNodeId;
        }
        newInterfaceNodeMap.set(linkId, bindings);
      }
    }

    // The incremental builder preserves graph identity for unchanged topology.
    // Configuration and rewiring changes must also invalidate parent embeddings,
    // even when an aggregate node/degree fingerprint stays the same.
    const structureChanged = this.templateGraph !== built.graph;

    this.templateGraph = built.graph;
    this.templateInstanceNodeMaps = built.instanceNodeMaps;
    this.templateTileNodeMap = new Map(
      built.tiles.map((tile) => [tile.id, { ...tile.logicNodeIds }]),
    );
    this.templateTileBundleMap = new Map(
      built.tiles.map((tile) => [
        tile.id,
        {
          laneNodeIds: tile.laneNodeIds,
          laneSignalIds: tile.laneSignalIds,
          laneSignalLabels: tile.laneSignalLabels,
          bundleSignalLabels: tile.bundleSignalLabels,
        },
      ]),
    );
    this.interfaceNodeMap = newInterfaceNodeMap;

    if (structureChanged) {
      this.templateGraphVersion++;
    }

    // Sequential stabilization breaks symmetry in feedback circuits (e.g. SR latches)
    // that would oscillate under two-phase simulation.
    if (this.continuationContext?.nativeContinuationHydratingChipId !== this.id)
      this.stabilizeTemplateGraph();
  }

  /** Structure labels are nets inside this instance only, never global channels. */
  private rebuildAdapterGraph(legacyForSelectors = false): void {
    const allowed = new Set<TileType>([
      TileType.SingleInterface,
      TileType.Structure,
      TileType.BundleInterface,
    ]);
    if (
      this.logicCircuit.circuitModel.tileManagerModel.tiles.length ||
      this.logicCircuit.circuitModel.chips.length ||
      this.structureCircuit.circuitModel.tileManagerModel.tiles.some(
        (tile) => !allowed.has(tile.tileType),
      )
    ) {
      this.templateGraph = null;
      this.interfaceNodeMap = new Map();
      this.adapterSignalIds = [];
      this.adapterSignalLabels = {};
      this.adapterSignalAliases = {};
      this.templateGraphVersion++;
      return;
    }
    const strict =
      !legacyForSelectors &&
      (this.continuationContext?.userPreferences?.bundleChipIdentityEnabled === true ||
        this.structureCircuit.circuitModel.tiles[0]?.context?.userPreferences
          ?.bundleChipIdentityEnabled === true);
    const graph = new LogicGraph(this.logicCircuit.logicGraph.ids);
    const nodes = new Map<string, NodeHandle>();
    const map = new Map<InterfaceLinkInstanceId, Partial<Record<Side, NodeHandle>>>();
    const links = this.interfaceLinks.filter(
      (link) =>
        link.type === InterfaceType.SINGLE && link.tiles[CircuitType.ChipStructure],
    );
    // Old files retain opaque adapter IDs in the legacy graph. Read its exact
    // per-link binding once; never infer cross-definition equivalence from labels.
    if (strict && links.some((link) => link.selectorSignalIds === undefined)) {
      this.rebuildAdapterGraph(true);
      const establishedSelectors = new Set(
        links.flatMap((link) => link.selectorSignalIds ?? []),
      );
      for (const link of links) {
        if (link.selectorSignalIds !== undefined) continue;
        const node = this.interfaceNodeMap.get(link.id)?.[Side.LEFT];
        const runtimeKey = node && this.templateGraph?.nodes.get(node)?.runtimeKey;
        const legacyId = runtimeKey && (JSON.parse(runtimeKey)[2] as string | undefined);
        link.selectorSignalIds =
          legacyId && !establishedSelectors.has(legacyId) ? [legacyId] : [];
      }
    }
    const sorted = [...links].sort(
      (a, b) =>
        (strict ? 0 : a.label.localeCompare(b.label, undefined, { numeric: true })) ||
        a.id.localeCompare(b.id),
    );
    const signature = () =>
      JSON.stringify(
        sorted.map((link) => [
          link.id,
          link.label,
          link.selectorSignalIds,
          link.tiles[CircuitType.ChipStructure]?.id,
          Object.hasOwn(this.adapterSignals, link.label.trim())
            ? this.adapterSignals[link.label.trim()]
            : null,
        ]),
      );
    const topologySignature = signature;
    const signatureWithPorts = () =>
      topologySignature() +
      JSON.stringify(
        this.structureCircuit.circuitModel.tileManagerModel.tiles.map((tile) => [
          tile.id,
          tile.tileType,
          tile.location,
          tile.meta.rotation,
        ]),
      );
    const fingerprint = `${strict}:` + signatureWithPorts();
    if (this.templateGraph && this._adapterFingerprint === fingerprint) return;
    const labels: Record<string, string> = {};
    const ids: string[] = [];
    const activeLabels = new Set(sorted.map((link) => link.label.trim()).filter(Boolean));
    const previousOwners = new Map(
      Object.entries(this.adapterSignalLabels).map(([id, label]) => [id, label]),
    );
    const usedIds = new Set<string>();
    for (const link of sorted) {
      const label = link.label.trim();
      if (!label && !strict) continue;
      // A definition member keeps its link identity through rename and reorder.
      const memberKey = strict ? `adapter:${JSON.stringify([this.id, link.id])}` : label;
      let nodeId = nodes.get(memberKey);
      if (!nodeId) {
        let signalId = strict
          ? memberKey
          : Object.hasOwn(this.adapterSignals, label)
            ? this.adapterSignals[label]
            : `adapter:${this.id}:${link.id}`;
        const previousOwner = previousOwners.get(signalId);
        if (
          !strict &&
          (usedIds.has(signalId) ||
            (previousOwner && previousOwner !== label && activeLabels.has(previousOwner)))
        ) {
          do {
            signalId = `adapter:${this.id}:${createInterfaceLinkInstanceId()}`;
          } while (usedIds.has(signalId) || previousOwners.has(signalId));
        }
        usedIds.add(signalId);
        const node = graph.addNode('WIRE');
        node.runtimeKey = JSON.stringify(['adapter', this.id, signalId]);
        nodeId = node.id;
        nodes.set(memberKey, nodeId);
        if (!strict)
          Object.defineProperty(this.adapterSignals, label, {
            value: signalId,
            enumerable: true,
            writable: true,
            configurable: true,
          });
        ids.push(signalId);
        labels[signalId] = label;
      }
      map.set(
        link.id,
        Object.fromEntries(Object.values(Side).map((side) => [side, nodeId])),
      );
    }
    const aliases: Record<string, string[]> = {};
    for (const link of sorted) {
      if (link.selectorSignalIds === undefined) continue;
      const node = map.get(link.id)?.[Side.LEFT];
      const runtimeKey = node && graph.nodes.get(node)?.runtimeKey;
      if (!runtimeKey) continue;
      const id = JSON.parse(runtimeKey)[2] as string;
      if (
        !strict &&
        !link.selectorSignalIds.includes(id) &&
        !sorted.some((other) => other !== link && other.selectorSignalIds?.includes(id))
      )
        link.selectorSignalIds.push(id);
      aliases[id] = [
        ...new Set([
          ...(aliases[id] ?? []),
          `adapter:${JSON.stringify([this.id, link.id])}`,
          ...link.selectorSignalIds,
        ]),
      ].filter((alias) => alias !== id);
    }
    this.adapterSignalAliases = aliases;
    this._adapterFingerprint = `${strict}:` + signatureWithPorts();
    this.adapterSignalIds = ids;
    this.adapterSignalLabels = labels;
    for (const tile of this.structureCircuit.circuitModel.tileManagerModel.tiles) {
      if (tile.tileType === TileType.SingleInterface) tile.adapterPin = true;
      if (tile.tileType !== TileType.BundleInterface) continue;
      tile.adapterPort = true;
      tile.adapterSignalIds = ids;
      tile.adapterSignalLabels = labels;
      tile.adapterSignalAliases = aliases;
      tile.laneNodeIds = { [Side.LEFT]: [...nodes.values()] };
      tile.laneSignalIds = { [Side.LEFT]: ids.map((id) => [id]) };
      tile.laneSignalLabels = { [Side.LEFT]: ids.map((id) => labels[id]) };
      tile.bundleSignalLabels = labels;
      tile.effectiveIoSides = {
        [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: true },
        [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
        [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
        [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
      };
      tile.renderVersion++;
    }
    this.templateGraph = graph;
    this.interfaceNodeMap = map;
    this.templateGraphVersion++;
  }

  private stabilizeTemplateGraph(): void {
    if (!this.templateGraph) return;

    const graph = this.templateGraph;
    const maxSteps = 100;

    // Settle passive propagation before evaluating the next gate. Diodes inside
    // nested NOR gates must propagate too, or both halves of a latch can keep
    // chasing stale feedback even during sequential initialization.
    // Only visit downstream nodes so independent circuits stay inexpensive.
    const isPassive = (type: string | undefined) => type === 'WIRE' || type === 'DIODE';
    const settlePassive = (sourceId?: NodeHandle) => {
      const queue: NodeHandle[] = [];
      const queued = new Set<NodeHandle>();
      const enqueue = (id: NodeHandle) => {
        if (!isPassive(graph.nodes.get(id)?.type) || queued.has(id)) return;
        queued.add(id);
        queue.push(id);
      };
      if (sourceId) {
        for (const edge of graph.getEdgesFrom(sourceId)) enqueue(edge.targetNodeId);
      } else {
        for (const [id, node] of graph.nodes) if (isPassive(node.type)) enqueue(id);
      }
      const limit = Math.max(1, graph.nodes.size * graph.nodes.size);
      for (let head = 0; head < queue.length && head < limit; head++) {
        const id = queue[head];
        queued.delete(id);
        const node = graph.nodes.get(id)!;
        const oldOutput = node.output;
        node.process(graph.getNodeInput(id), 16);
        if (node.output !== oldOutput) {
          for (const edge of graph.getEdgesFrom(id)) enqueue(edge.targetNodeId);
        }
      }
    };

    for (let step = 0; step < maxSteps; step++) {
      let changed = false;

      settlePassive();

      // Process logic nodes sequentially, settling passive paths after each change
      // so downstream nodes see updated values immediately.
      for (const [id, node] of graph.nodes) {
        if (isPassive(node.type)) continue;
        const input = graph.getNodeInput(id);
        const oldOutput = node.output;
        node.process(input, 16);
        if (node.output !== oldOutput) {
          changed = true;
          settlePassive(id);
        }
      }

      if (!changed) break;
    }
  }
}
